import React from 'react';
import { useNavigate } from 'react-router-dom';
import Causes from './Causes';
import './Blog.css';

const Blog = () => {
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate('/contact');
  };
  const statData = [
    {
      title: "5+ Years of Ongoing Concern",
      text: "A persistent challenge in Indian dairy farming, affecting productivity and animal health across rural and semi-urban regions.",
      img: "/images/blog1.jpg"
    },
    {
      title: "18+ Livestock Health Issues Linked",
      text: "Often occurs alongside metabolic disorders, lameness, and reproductive issues, compounding the impact on dairy herds.",
      img: "/images/blog2.jpg"
    },
    {
      title: "₹13,000 Cr+ Annual Economic Loss",
      text: "India loses over ₹13,000 crores every year due to mastitis — from reduced milk yield, discarded milk, veterinary treatment costs, and long-term udder damage.",
      img: "/images/blog3.jpg"
    },
    {
      title: "20K+ Farmers Affected Across India",
      text: "Tens of thousands of dairy farmers suffer losses, lower income, and reduced herd productivity due to mastitis mismanagement.",
      img: "/images/blog4.jpg"
    },
    {
      title: "80% Cases Go Undiagnosed",
      text: "Subclinical mastitis is widespread in India, with over 80% of cases going undetected due to lack of routine testing and awareness.",
      img: "/images/blog5.jpg"
    },
    {
      title: "Low Access to Veterinary Support",
      text: "In many rural regions, farmers lack timely access to veterinary help, leading to delayed treatment and increased spread of infection.",
      img: "/images/blog6.jpg"
    }
  ];

  return (
    <section id="blog" className="blog-section">
      {/* Banner at the top */}
      <div className="knowledge-hub-banner">
        <h1>Mastitis - How it started?</h1>
      </div>

      <div className="blog-container">
        {/* 1. Mastitis – How It Started */}
        <div className="blog-header" data-aos="fade-up">
          <h2>Our Mission is Livestock Health and Dairy Sustainability</h2>
          <p>
            Mastitis and other livestock diseases pose serious challenges to India's dairy industry. Our initiative began with a commitment to empower farmers, veterinarians, and dairy stakeholders with knowledge and solutions that prioritize animal health, milk quality, and economic resilience.
          </p>
          <p>
            Mastitis, in particular, is a silent threat—often going unnoticed until significant damage is done. By promoting awareness, early detection, and preventive care, we aim to reduce losses and enhance dairy productivity across the country.
          </p>
        </div>

        {/* 2. Mastitis? */}
        <div className="mastitis-info" data-aos="fade-up">
          <h2>Mastitis?</h2>
          <p>
                Mastitis is a serious inflammatory condition of the udder in dairy animals, primarily caused by bacterial infections. It is one of the leading health challenges in India's dairy industry-affecting both the productivity and welfare of animals.

             When mastitis occurs, the udder becomes swollen, painful, and sensitive to touch. Infected animals may produce discolored, clotted, or reduced-quality milk, and in severe cases, the infection can cause permanent udder damage. Even when no visible signs are present, subclinical mastitis can silently reduce milk yield and spread within the herd.
          </p>
          <p>
            When a cow suffers from mastitis, the udder becomes swollen, painful, and may produce abnormal milk — affecting both the quality and quantity of milk. Early detection, good milking hygiene, and proper udder care are key to preventing and controlling this invisible threat.
          </p>
          <p>Mastitis not only compromises animal health — it leads to significant economic losses for farmers due to reduced milk output, discarded milk, veterinary costs, and lowered reproductive performance.</p>
        </div>

        {/* 3. Mastitis – The Cause */}
        <div className="mastitis-info" data-aos="fade-up">
          <h2>Mastitis – The Cause</h2>
          <p>
            Mastitis doesn't just happen — it's triggered by a combination of infectious bacteria and poor udder hygiene. Whether clinical or subclinical, mastitis usually begins when harmful pathogens enter the teat canal, often through milking practices, dirty environments, or injuries. Understanding the root causes is the first step in preventing this silent profit-drainer.
          </p>
          <Causes />
        </div>

        {/* 4. Mastitis in India – A Glance */}
        <div data-aos="fade-up">
          <h2 className="mastitis-glance-title">Mastitis in India — A Glance</h2>
          <p className="glance-description">
            Mastitis remains one of the most widespread and economically damaging diseases affecting India's dairy industry. From invisible subclinical infections to major productivity losses, the impact is deep and long-lasting. Here's a quick look at the scale and seriousness of the problem.
          </p>
          <div className="stat-cards">
            {statData.map((card, i) => (
              <div className="stat-card hover-overlay" key={i}>
                <div className="image-container">
                  <img src={card.img} alt={card.title} />
                  <div className="overlay">
                    <div className="overlay-content">
                      <h3>{card.title}</h3>
                      <p>{card.text}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 5. A Healthy Udder. Happy Farmer */}
        <div className="udder-content" data-aos="fade-up">
          <h2 className="section-title">Healthy Udder, Happy Farmer</h2>
          <p className="section-subtitle">Mastitis may start small - but its impact is huge.</p>
          
          <div className="udder-steps">
            <div className="udder-step" data-aos="fade-right">
              <div className="circlle">1</div>
              <div className="content-box">
                <h3>Healthy cows start with a clean routine</h3>
                <ul>
                  <li>Regular milking hygiene and good barn conditions reduce infection risk.</li>
                  <li>A clean environment is the first defense against mastitis.</li>
                  <li>Daily care routines protect animal health and milk quality.</li>
                </ul>
              </div>
            </div>

            <div className="udder-step right" data-aos="fade-left">
              <div className="circlle">2</div>
              <div className="content-box">
                <h3>Mastitis begins where bacteria break in</h3>
                <ul>
                  <li>When bacteria enter the udder, they cause pain, inflammation, and a drop in milk quality.</li>
                  <li>Poor hygiene during or after milking can introduce infection.</li>
                  <li>Once inside, bacteria multiply fast — early care matters.</li>
                </ul>
              </div>
            </div>

            <div className="udder-step" data-aos="fade-right">
              <div className="circlle">3</div>
              <div className="content-box">
                <h3>Invisible infections, visible damage</h3>
                <ul>
                  <li>Left untreated, mastitis can lead to permanent udder damage and major economic loss.</li>
                  <li>Inflammation and swelling are clear signs of clinical mastitis. Subclinical mastitis may show no visible symptoms — but it's still harmful.</li>
                  <li>Infection affects both cow comfort and production.</li>
                </ul>
              </div>
            </div>

            <div className="udder-step right" data-aos="fade-left">
              <div className="circlle">4</div>
              <div className="content-box">
                <h3>Mastitis costs more than milk</h3>
                <ul>
                  <li>From discarded milk to high vet bills, the losses add up quickly.</li>
                  <li>Chronic infections may lead to permanent udder damage.</li>
                  <li>Each case of mastitis reduces milk yield and farmer profits.</li>
                </ul>
              </div>
            </div>

            <div className="udder-step" data-aos="fade-right">
              <div className="circlle">5</div>
              <div className="content-box">
                <h3>But the good news? It's preventable with proper hygiene and care.</h3>
                <ul>
                  <li>Pre- and post-milking teat disinfection lowers infection chances.</li>
                  <li>Clean housing/ farm and regular health checks prevent bacterial buildup.</li>
                </ul>
              </div>
            </div>

            <div className="udder-step right" data-aos="fade-left">
              <div className="circlle">6</div>
              <div className="content-box">
                <h3>"A few small steps today can protect your cows, milk, and profits tomorrow."</h3>
                <ul>
                  <li>Good prevention means better milk yield and animal comfort.</li>
                  <li>Protecting livestock health protects your livelihood.</li>
                  <li>Mastitis-free herds boost farm income and reputation.</li>
                </ul>
              </div>
            </div>

            <div className="udder-step" data-aos="fade-right">
              <div className="circlle">7</div>
              <div className="content-box">
                <h3>"Stop mastitis before it starts. Prevention is protection."</h3>
                <ul>
                  <li>Act early, treat properly, and prevent routinely.</li>
                  <li>Don't wait for symptoms — safeguard your herd now.</li>
                  <li>Make prevention part of your everyday dairy practice.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

      </div>
      
      {/* 6. Final Quote - Outside container for full width */}
      <div className="final-quote" data-aos="fade-up">
        <h2>"What you don't detect can cost you most."</h2>
        <p>Mastitis silently impacts your dairy herd. Our solution is built to prevent it — smartly, sustainably, and simply.</p>
        <p className="curious-text">Curious how we do it?</p>
        <button className="contact-cta-btn" onClick={handleContactClick}>Contact Us to Know More</button>
      </div>
    </section>
  );
};

export default Blog;